import type { TrainingGating } from "@/api/models"
import type {
  OnboardingProgress,
  OnboardingStep,
  UISettings,
  UserSettings,
} from "@/types/user-settings"

import { hasCurrentM365Decision } from "@/features/ai-explorer/m365-approval"

export const ONBOARDING_VERSION = 2
export const CURRENT_GDPR_VERSION = "1.0"
export const GDPR_EXPIRY_MONTHS = 6

const KNOWN_STEPS = new Set<OnboardingStep>([
  "welcome",
  "compliance",
  "training",
  "m365",
  "personalization",
  "ready",
])

const BLOCKED_RETURN_PATHS = ["/onboarding", "/redirect", "/auth/", "/signed-out"]
const TRAINING_DISMISS_KEY = "training_gating_dismissed"

let completionReadyLatched = false
const completionReadyListeners = new Set<() => void>()

export function readOnboardingCompletionReady(): boolean {
  return completionReadyLatched
}

export function subscribeOnboardingCompletionReady(onStoreChange: () => void): () => void {
  completionReadyListeners.add(onStoreChange)
  return () => {
    completionReadyListeners.delete(onStoreChange)
  }
}

function emitOnboardingCompletionReady(): void {
  for (const listener of completionReadyListeners) listener()
}

export function latchOnboardingCompletionReady(): void {
  if (completionReadyLatched) return
  completionReadyLatched = true
  emitOnboardingCompletionReady()
}

export function clearOnboardingCompletionReady(): void {
  if (!completionReadyLatched) return
  completionReadyLatched = false
  emitOnboardingCompletionReady()
}

export function getOnboardingFlowSteps(
  showTraining: boolean,
  showM365: boolean = false
): OnboardingStep[] {
  return [
    "welcome",
    "compliance",
    ...(showTraining ? (["training"] as const) : []),
    ...(showM365 ? (["m365"] as const) : []),
    "personalization",
    "ready",
  ]
}

/**
 * Step the wizard should treat as current while a Microsoft 365 decision is
 * being saved. The settings mutation applies the decision optimistically, so
 * the resolver would move past the consent step before the write is
 * acknowledged (and bounce back if it fails). From the live consent step, hold
 * on it until the write settles. In review mode (Back from personalization,
 * then re-deciding) the resolved step is already personalization and must stay
 * so: treating it as a step change there resets the About Me draft.
 */
export function holdOnboardingStep(
  resolvedStep: OnboardingStep,
  { decisionPending, reviewing }: { decisionPending: boolean; reviewing: boolean }
): OnboardingStep {
  return decisionPending && !reviewing ? "m365" : resolvedStep
}

export function isConfiguredAdminPath(
  routes: Array<{ component: string; path: string }> | undefined,
  pathname: string
): boolean {
  const adminPath = routes?.find((route) => route.component === "admin-dashboard")?.path
  return Boolean(adminPath && pathname.startsWith(adminPath))
}

export function readSoftTrainingDismissed(): boolean {
  try {
    return typeof window !== "undefined" && sessionStorage.getItem(TRAINING_DISMISS_KEY) === "true"
  } catch {
    return false
  }
}

export function dismissSoftTrainingForSession(): void {
  try {
    sessionStorage.setItem(TRAINING_DISMISS_KEY, "true")
  } catch {
    // Restricted storage only means the reminder may return next navigation.
  }
}

export function isGdprAccepted(ui: UISettings | undefined, now: number = Date.now()): boolean {
  if (!ui || ui.gdpr_accepted_version !== CURRENT_GDPR_VERSION) return false
  if (!ui.gdpr_accepted_timestamp) return true
  const acceptedAt = Date.parse(ui.gdpr_accepted_timestamp)
  if (Number.isNaN(acceptedAt)) return false
  const ageInMonths = (now - acceptedAt) / (1000 * 60 * 60 * 24 * 30)
  return ageInMonths < GDPR_EXPIRY_MONTHS
}

export function safeReturnTo(value: string | null | undefined, fallback: string): string {
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.length > 2048 ||
    BLOCKED_RETURN_PATHS.some((path) => value.startsWith(path))
  ) {
    return fallback
  }
  return value
}

export function withoutForceOnboardingFlag(value: string): string {
  try {
    const url = new URL(value, "https://onboarding.local")
    url.searchParams.delete("forceOnboarding")
    url.searchParams.delete("onboarding")
    url.searchParams.delete("completed")
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return value
  }
}

export function withOnboardingCompletedFlag(value: string): string {
  try {
    const url = new URL(value, "https://onboarding.local")
    url.searchParams.set("completed", "1")
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return value
  }
}

export function applyOnboardingCompletionToUserSettings(
  current: UserSettings | undefined,
  result: {
    completed_at: string | null
    whats_new_last_seen_version: string | null
  }
): UserSettings | undefined {
  if (!current) return current
  const completedAt = result.completed_at ?? current.data.onboarding_completed_at ?? null
  const previous = current.data.onboarding_progress
  return {
    ...current,
    data: {
      ...current.data,
      onboarding_completed_at: completedAt,
      onboarding_progress: {
        version: ONBOARDING_VERSION,
        current_step: "ready",
        started_at: previous?.started_at || completedAt || "",
        ...(previous?.return_to ? { return_to: previous.return_to } : {}),
      },
      ui: result.whats_new_last_seen_version
        ? {
            ...current.data.ui,
            whats_new_last_seen_version: result.whats_new_last_seen_version,
          }
        : current.data.ui,
    },
  }
}

export function getPersistedOnboardingStep(
  progress: OnboardingProgress | undefined
): OnboardingStep {
  if (progress?.version !== ONBOARDING_VERSION || !KNOWN_STEPS.has(progress.current_step)) {
    return "welcome"
  }
  return progress.current_step
}

export type OnboardingResolution = {
  step: OnboardingStep | null
  isMandatory: boolean
}

export function resolveOnboardingStep({
  completedAt,
  progress,
  ui,
  training,
  showCompleted,
  softTrainingDismissed = false,
  m365Enabled = false,
}: {
  completedAt: string | null | undefined
  progress: OnboardingProgress | undefined
  ui: UISettings | undefined
  training: TrainingGating | undefined
  showCompleted?: boolean
  softTrainingDismissed?: boolean
  /**
   * Tenant has the Microsoft 365 tool. The consent step is derived from the
   * user's decision state, not from persisted progress: it shows for every
   * not-yet-completed user with no current-version decision, and both wizard
   * buttons persist a decision, so it can never be skipped or replayed.
   */
  m365Enabled?: boolean
}): OnboardingResolution {
  const completed = Boolean(completedAt)
  const persisted = getPersistedOnboardingStep(progress)
  const complianceAccepted = isGdprAccepted(ui)
  const trainingEnabled = training?.enabled === true
  const trainingComplete = training?.ai_awareness_completed === true
  const hasLegacyTraining = training?.has_legacy_training === true
  const hardTrainingOutstanding = trainingEnabled && !trainingComplete && !hasLegacyTraining

  if (completed && showCompleted && complianceAccepted && !hardTrainingOutstanding) {
    return { step: "ready", isMandatory: false }
  }

  // A genuinely new user gets the welcome moment before policy content. Once
  // they have advanced, required policy always takes precedence over the
  // persisted optional step.
  if (!completed && persisted === "welcome") {
    return { step: "welcome", isMandatory: false }
  }

  if (!complianceAccepted) {
    return { step: "compliance", isMandatory: true }
  }

  if (hardTrainingOutstanding) {
    return { step: "training", isMandatory: true }
  }

  if (
    completed &&
    trainingEnabled &&
    !trainingComplete &&
    hasLegacyTraining &&
    !softTrainingDismissed
  ) {
    return { step: "training", isMandatory: false }
  }

  if (completed) {
    return { step: null, isMandatory: false }
  }

  if (
    trainingEnabled &&
    !trainingComplete &&
    (persisted === "compliance" || persisted === "training")
  ) {
    return { step: "training", isMandatory: false }
  }

  // Consent lives in the wizard so a new user meets exactly one popup. Users who
  // completed onboarding before this step existed returned above, and keep the
  // standalone consent dialog on the chat home instead.
  if (m365Enabled && !hasCurrentM365Decision(ui)) {
    return { step: "m365", isMandatory: false }
  }

  if (persisted === "ready") {
    return { step: "ready", isMandatory: false }
  }

  return { step: "personalization", isMandatory: false }
}
